import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { api } from "../api";
import type { CleanRow } from "../types";
import { Icon, IconButton } from "./Icons";

/** from: the card whose text was reused. parent: the card this one forks (drawn as a link). */
export interface ComposerSeed { text?: string; from?: string; parent?: string }

interface Props {
  seed: ComposerSeed;
  /** The target scenario, named when a large batch asks for confirmation. */
  scenario?: string;
  onCancel: () => void;
  /** Saves and runs one intent. Resolves when started; rejects with a message to show. */
  onCreate: (text: string) => Promise<void>;
  /** Saves several at once (run = start them all, else drafts). Omit to allow only one. */
  onCreateMany?: (texts: string[], run: boolean) => Promise<void>;
}

interface Row { id: number; text: string; suggestion?: string; reason?: string }

const MAX_ROWS = 50;
const CONFIRM_AT = 10;
const CLEAN_WAIT_MS = 2500;   // a suggestion that takes longer than this isn't worth the wait
const HEADER_WORDS = /^(intents?|instructions?|descriptions?|actions?|requests?|questions?|tasks?|products?|skus?|parts?|changes?|months?|periods?|comments?|notes?|text)$/i;

let nextId = 1;
const row = (text = ""): Row => ({ id: nextId++, text });

/** Excel / sheet clipboard text -> rows of cells (tabs between cells, quoted cells may hold newlines). */
export function parseClipboard(text: string): string[][] {
  const out: string[][] = [];
  let cell = "", cells: string[] = [], quoted = false;
  const src = text.replace(/\r\n?/g, "\n");
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"' && cell === "") quoted = true;
    else if (ch === "\t") { cells.push(cell); cell = ""; }
    else if (ch === "\n") { cells.push(cell); out.push(cells); cells = []; cell = ""; }
    else cell += ch;
  }
  if (cell || cells.length) { cells.push(cell); out.push(cells); }
  return out.map((r) => r.map((c) => c.replace(/\s+/g, " ").trim())).filter((r) => r.some(Boolean));
}

/** One sentence per pasted row: cells joined with " · " (the cleanup suggests a plainer wording). */
const joinCells = (cells: string[]) => cells.filter(Boolean).join(" · ");
const looksLikeHeader = (cells: string[]) =>
  cells.filter(Boolean).length > 0 && cells.filter(Boolean).every((c) => HEADER_WORDS.test(c.replace(/[^a-z ]/gi, "").trim()));

/**
 * Entry box. One intent: type and press Enter (Shift+Enter for a new line, Esc cancels).
 * Many: paste rows from Excel (or press ⊞) and the box becomes a one-column list, one intent
 * per row. Either way gentiK tidies the text and, when a clearer wording is a meaningful
 * change that keeps the intent, offers it as a suggestion to take or leave.
 */
export default function InlineComposer({ seed, scenario, onCancel, onCreate, onCreateMany }: Props) {
  const [mode, setMode] = useState<"single" | "list">("single");
  const [text, setText] = useState(seed.text ?? "");
  const [rows, setRows] = useState<Row[]>([]);
  const [suggestion, setSuggestion] = useState<{ text: string; reason?: string } | null>(null);
  const [skippedHeader, setSkippedHeader] = useState<string | null>(null);
  const [cleaning, setCleaning] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);
  const rowRefs = useRef(new Map<number, HTMLTextAreaElement>());
  const focusRow = useRef<{ id: number; at: "start" | "end" } | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
  }, []);
  // Grow with the text instead of scrolling.
  useEffect(() => {
    const el = ref.current;
    if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; }
  }, [text, mode]);
  useLayoutEffect(() => {
    for (const el of rowRefs.current.values()) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; }
    const f = focusRow.current;
    if (f) {
      const el = rowRefs.current.get(f.id);
      if (el) { el.focus(); const p = f.at === "start" ? 0 : el.value.length; el.setSelectionRange(p, p); }
      focusRow.current = null;
    }
  }, [rows]);

  // ── cleanup suggestions ───────────────────────────────────────────────────
  const fetchClean = async (texts: string[]): Promise<CleanRow[] | null> => {
    try {
      const timeout = new Promise<null>((resolve) => window.setTimeout(() => resolve(null), CLEAN_WAIT_MS));
      const res = await Promise.race([api.cleanIntents(texts), timeout]);
      return res ? res.rows : null;
    } catch { return null; }
  };

  const suggestForRows = async (list: Row[]) => {
    const targets = list.filter((r) => r.text.trim());
    if (!targets.length) return;
    setCleaning(true);
    const res = await fetchClean(targets.map((r) => r.text));
    setCleaning(false);
    if (!res) return;
    const byId = new Map(targets.map((r, i) => [r.id, res[i]]));
    setRows((cur) => cur.map((r) => {
      const c = byId.get(r.id);
      if (!c || c.original !== r.text) return r;   // edited since: the suggestion would be stale
      return { ...r, text: c.cleaned || r.text, suggestion: c.changed ? c.suggested : undefined, reason: c.reason };
    }));
  };

  // ── single ────────────────────────────────────────────────────────────────
  const submitSingle = async (value: string, skipClean = false) => {
    const t = value.trim();
    if (busy || !t) return;
    setBusy(true);
    setError("");
    if (!skipClean) {
      setCleaning(true);
      const res = await fetchClean([t]);
      setCleaning(false);
      const c = res?.[0];
      if (c?.changed && c.suggested && c.suggested !== t) {
        setText(c.cleaned || t);
        setSuggestion({ text: c.suggested, reason: c.reason });
        setBusy(false);
        return;   // the planner decides: use the suggestion or keep their own
      }
      if (c?.cleaned) value = c.cleaned;
    }
    try {
      await onCreate(value.trim());
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  // ── list ──────────────────────────────────────────────────────────────────
  const toList = (texts: string[], header: string | null = null) => {
    const start = text.trim() && mode === "single" ? [row(text.trim())] : [];
    const next = [...start, ...texts.map((t) => row(t))].slice(0, MAX_ROWS);
    if (!next.length) next.push(row());
    setMode("list");
    setRows(next);
    setSuggestion(null);
    setSkippedHeader(header);
    focusRow.current = { id: next[next.length - 1].id, at: "end" };
    void suggestForRows(next);
  };

  const pastedRows = (clip: string): { texts: string[]; header: string | null } | null => {
    if (!/[\n\t]/.test(clip.trim())) return null;   // one plain line: an ordinary paste
    const parsed = parseClipboard(clip);
    if (parsed.length < 2 && !(parsed[0]?.length > 1)) return null;
    let header: string | null = null;
    if (parsed.length > 1 && looksLikeHeader(parsed[0])) header = joinCells(parsed.shift()!);
    return { texts: parsed.map(joinCells).filter(Boolean), header };
  };

  const onPasteSingle = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!onCreateMany) return;
    const got = pastedRows(e.clipboardData.getData("text/plain"));
    if (!got || got.texts.length < 2) return;
    e.preventDefault();
    toList(got.texts, got.header);
  };

  const onPasteRow = (e: React.ClipboardEvent<HTMLTextAreaElement>, at: number) => {
    const got = pastedRows(e.clipboardData.getData("text/plain"));
    if (!got) return;
    e.preventDefault();
    // Like Excel: fill downward from this row (an empty row is replaced, not pushed down).
    const incoming = got.texts.map((t) => row(t));
    setRows((cur) => {
      const replace = cur[at] && !cur[at].text.trim() ? 1 : 0;
      const next = [...cur.slice(0, at + 1 - replace), ...incoming, ...cur.slice(at + 1)].slice(0, MAX_ROWS);
      focusRow.current = { id: incoming[incoming.length - 1].id, at: "end" };
      return next;
    });
    if (got.header) setSkippedHeader(got.header);
    void suggestForRows(incoming);
  };

  const setRowText = (id: number, value: string) =>
    setRows((cur) => cur.map((r) => (r.id === id ? { ...r, text: value, suggestion: undefined } : r)));
  const takeSuggestion = (id: number) =>
    setRows((cur) => cur.map((r) => (r.id === id && r.suggestion ? { ...r, text: r.suggestion, suggestion: undefined } : r)));
  const dropSuggestion = (id: number) =>
    setRows((cur) => cur.map((r) => (r.id === id ? { ...r, suggestion: undefined } : r)));

  const onRowKey = (e: React.KeyboardEvent<HTMLTextAreaElement>, i: number) => {
    const el = e.currentTarget;
    const r = rows[i];
    if (e.key === "Escape") { e.stopPropagation(); onCancel(); return; }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (rows.length >= MAX_ROWS) return;
      const fresh = row();
      setRows((cur) => [...cur.slice(0, i + 1), fresh, ...cur.slice(i + 1)]);
      focusRow.current = { id: fresh.id, at: "start" };
      if (r.text.trim()) void suggestForRows([r]);
      return;
    }
    if (e.key === "Backspace" && !r.text && rows.length > 1) {
      e.preventDefault();
      const prev = rows[Math.max(0, i - 1)];
      setRows((cur) => cur.filter((x) => x.id !== r.id));
      focusRow.current = { id: prev.id === r.id ? rows[1].id : prev.id, at: "end" };
      return;
    }
    if (e.key === "ArrowUp" && el.selectionStart === 0 && i > 0) {
      e.preventDefault(); focusRow.current = { id: rows[i - 1].id, at: "end" }; setRows((cur) => [...cur]);
    }
    if (e.key === "ArrowDown" && el.selectionEnd === el.value.length && i < rows.length - 1) {
      e.preventDefault(); focusRow.current = { id: rows[i + 1].id, at: "start" }; setRows((cur) => [...cur]);
    }
  };

  const filled = rows.map((r) => r.text.trim()).filter(Boolean);
  const pendingSuggestions = rows.filter((r) => r.suggestion).length;

  const submitList = async (run: boolean) => {
    if (busy || !filled.length || !onCreateMany) return;
    if (run && filled.length >= CONFIRM_AT && !confirm) { setConfirm(true); return; }
    setBusy(true);
    setError("");
    try {
      await onCreateMany(filled, run);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
      setConfirm(false);
    }
  };

  // ── render ────────────────────────────────────────────────────────────────
  if (mode === "list") {
    return (
      <div className={`canvas-overlay entry-box list${busy ? " busy" : ""}${error ? " has-error" : ""}`}>
        <div className="entry-list-head small">
          <span><b>{filled.length}</b> intent{filled.length === 1 ? "" : "s"}{cleaning ? " · tidying…" : ""}</span>
          <span className="grow" />
          {pendingSuggestions > 1 && (
            <button type="button" className="link-btn" onClick={() => setRows((cur) => cur.map((r) => (r.suggestion ? { ...r, text: r.suggestion, suggestion: undefined } : r)))}>
              Use all {pendingSuggestions} suggestions
            </button>
          )}
          <IconButton icon="close" label="Cancel" className="card-action" onClick={onCancel} />
        </div>
        {skippedHeader && (
          <div className="entry-note small muted">
            Skipped a header row (“{skippedHeader}”).{" "}
            <button type="button" className="link-btn" onClick={() => { const h = skippedHeader; setSkippedHeader(null); setRows((cur) => [row(h), ...cur]); }}>Undo</button>
          </div>
        )}
        <ol className="entry-rows">
          {rows.map((r, i) => (
            <li key={r.id} className="entry-row">
              <span className="entry-n">{i + 1}</span>
              <div className="entry-cell">
                <textarea ref={(el) => { if (el) rowRefs.current.set(r.id, el); else rowRefs.current.delete(r.id); }}
                          rows={1} value={r.text} disabled={busy} aria-label={`Intent ${i + 1}`}
                          placeholder={i === rows.length - 1 ? "Type or paste more…" : ""}
                          onChange={(e) => setRowText(r.id, e.target.value)}
                          onKeyDown={(e) => onRowKey(e, i)} onPaste={(e) => onPasteRow(e, i)}
                          onBlur={() => { if (r.text.trim() && !r.suggestion && r.reason === undefined) void suggestForRows([r]); }} />
                {r.suggestion && (
                  <Suggested text={r.suggestion} reason={r.reason} onUse={() => takeSuggestion(r.id)} onKeep={() => dropSuggestion(r.id)} />
                )}
              </div>
            </li>
          ))}
        </ol>
        <div className="entry-actions">
          {confirm
            ? <span className="small">Run {filled.length} intents against <b>{scenario || "the target scenario"}</b>?</span>
            : <span className="small muted">Enter adds a row · paste fills down</span>}
          <span className="grow" />
          {!confirm && (
            <button type="button" className="btn ghost sm" disabled={busy || !filled.length} onClick={() => submitList(false)}>
              Add {filled.length} as drafts
            </button>
          )}
          {confirm && <button type="button" className="btn ghost sm" onClick={() => setConfirm(false)}>Back</button>}
          <button type="button" className="btn primary sm" disabled={busy || !filled.length} onClick={() => submitList(true)}>
            <Icon name="run" size={13} /> {confirm ? `Yes, run ${filled.length}` : `Run ${filled.length}`}
          </button>
        </div>
        {error && <div className="entry-error" role="alert">{error}</div>}
      </div>
    );
  }

  return (
    <div className={`canvas-overlay entry-box${busy || cleaning ? " busy" : ""}${error ? " has-error" : ""}`}>
      <div className="entry-single">
        <textarea
          ref={ref} rows={1} value={text} disabled={busy || cleaning} aria-label="Type an intent or ask a question, then press Enter"
          placeholder="Type an intent or ask me a question"
          onChange={(e) => { setText(e.target.value); setSuggestion(null); }}
          onPaste={onPasteSingle}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (suggestion) void submitSingle(e.ctrlKey || e.metaKey ? text : suggestion.text, true);
              else void submitSingle(text);
            }
            if (e.key === "Escape") { e.stopPropagation(); if (suggestion) setSuggestion(null); else onCancel(); }
          }}
        />
        {onCreateMany && !suggestion && (
          <IconButton icon="addRows" label="Add more: enter several intents as a list (or paste rows from Excel)"
                      className="card-action entry-more" onClick={() => toList([""])} />
        )}
      </div>
      {cleaning && <div className="entry-note small muted">Tidying…</div>}
      {suggestion && (
        <Suggested text={suggestion.text} reason={suggestion.reason} hint="Enter uses it · Ctrl+Enter keeps yours"
                   onUse={() => void submitSingle(suggestion.text, true)} onKeep={() => void submitSingle(text, true)} />
      )}
      {error && <div className="entry-error" role="alert">{error}</div>}
    </div>
  );
}

/** A clearer wording gentiK proposes; the planner takes it or keeps their own. */
function Suggested({ text, reason, hint, onUse, onKeep }: {
  text: string; reason?: string; hint?: string; onUse: () => void; onKeep: () => void;
}) {
  return (
    <div className="suggested" role="group" aria-label="Suggested wording">
      <div className="suggested-label">Suggested{reason ? <span className="muted"> · {reason}</span> : null}</div>
      <div className="suggested-text">{text}</div>
      <div className="suggested-actions">
        {hint && <span className="small muted">{hint}</span>}
        <span className="grow" />
        <button type="button" className="btn ghost sm" onClick={onKeep}>Keep mine</button>
        <button type="button" className="btn sm" onClick={onUse}>Use this</button>
      </div>
    </div>
  );
}
